<template>
    <div>
        <Modal
            :show="shown"
            :title="this.$t('roomConfiguration.acousticSettings.title')"
            :close-callback="close"
            :esc-handler="close"
            :enter-handler="ok"
            style-class="modal-acoustic"
        >
            <span v-focus-inserted tabindex="1" aria-hidden="true"></span>
            <form>
                <label>{{ $t('roomConfiguration.acousticSettings.status') }}</label>
                <button
                    :class="isAcousticEnabled ? 'icon-mic' : 'icon-no-mic'"
                    :title="$t('roomConfiguration.acousticSettings.topLabel')"
                    @click.prevent="toggleAcoustic"
                ></button>
            </form>
            <form>
                <div>
                    <label>{{ $t('roomConfiguration.acousticSettings.soundLevel') }}</label>
                    <input
                        id="forAT_AcousticSetts-soundLevelRange"
                        v-model="soundLevel"
                        type="range"
                        step="1"
                        min="0"
                        max="100"
                        list="numbers"
                        :disabled="!isAcousticEnabled"
                    />
                    <span></span>
                    <datalist id="numbers">
                        <option value="0 to 100">0</option>
                        <option>20</option>
                        <option>40</option>
                        <option>60</option>
                        <option>80</option>
                        <option>100</option>
                    </datalist>
                </div>
                <div>
                    <span v-if="!$v.soundLevel.required" class="tooltiptext">{{
                        $t('validators.minimumLength')
                    }}</span>
                    <span v-if="!$v.soundLevel.between" class="tooltiptext">{{
                        $t('roomConfiguration.acousticSettings.valueInterval', {
                            val1: MIN_SOUND_LEVEL,
                            val2: MAX_SOUND_LEVEL,
                        })
                    }}</span>
                    <input
                        id="forAT_AcousticSetts-soundLevelNumber"
                        v-model="soundLevel"
                        v-numeric-only
                        maxlength="3"
                        :disabled="!isAcousticEnabled"
                        :class="{ tooltiperror: $v.soundLevel.$invalid }"
                        @input="$v.soundLevel.$touch()"
                    />
                </div>
            </form>
            <form>
                <div>
                    <label>{{ $t('roomConfiguration.acousticSettings.activationTime') }}</label>

                    <input
                        id="forAT_AcousticSetts-activationTimeRange"
                        v-model="activationTime"
                        step="1"
                        min="100"
                        max="3000"
                        type="range"
                        list="numbers2"
                        :disabled="!isAcousticEnabled"
                    />
                    <span></span>
                    <datalist id="numbers2">
                        <option>100</option>
                        <option>1000</option>
                        <option>2000</option>
                        <option>3000</option>
                    </datalist>
                </div>
                <div>
                    <span v-if="!$v.activationTime.between && $v.activationTime.required" class="tooltiptext">{{
                        $t('roomConfiguration.acousticSettings.valueInterval', {
                            val1: ACTIVATION_TIME_FROM,
                            val2: ACTIVATION_TIME_TO,
                        })
                    }}</span>
                    <span v-if="!$v.activationTime.required" class="tooltiptext">
                        {{ $t('validators.minimumLength') }}</span
                    >
                    <input
                        id="forAT_AcousticSetts-activationTimeNumber"
                        v-model="activationTime"
                        v-numeric-only
                        max="3000"
                        maxlength="4"
                        :disabled="!isAcousticEnabled"
                        :class="{ tooltiperror: $v.activationTime.$invalid }"
                        @input="$v.activationTime.$touch()"
                    />
                </div>
            </form>
            <div slot="footer">
                <div class="pull-right">
                    <button class="btn btn-ok" :disabled="isOkDisabled" @click="ok">
                        {{ $t('common.ok') }}
                    </button>
                    <button class="btn btn-cancel" @click="close">{{ $t('common.cancel') }}</button>
                </div>
            </div>
        </Modal>
    </div>
</template>

<script type="text/babel">
import Modal from './../common/Modal.vue';
import Location from '../room-configuration/Location';
import { required, between } from 'vuelidate/lib/validators';
import { MIN_SOUND_LEVEL, MAX_SOUND_LEVEL, ACTIVATION_TIME_FROM, ACTIVATION_TIME_TO } from '../../constants';
import commonDirectives from '../common/commonDirectives';

export default {
    components: {
        Modal,
    },

    mixins: [commonDirectives],

    constants: {
        MIN_SOUND_LEVEL,
        MAX_SOUND_LEVEL,
        ACTIVATION_TIME_FROM,
        ACTIVATION_TIME_TO,
    },

    props: {
        shown: {
            type: Boolean,
            default: false,
        },

        location: {
            type: Location,
            required: true,
        },
    },

    data() {
        return {
            isAcousticEnabled: undefined,
            activationTime: undefined,
            soundLevel: undefined,
            isChanged: false,
        };
    },

    computed: {
        isOkDisabled() {
            return !this.isChanged || this.$v.acousticGroup.$invalid;
        },
    },

    validations() {
        return {
            activationTime: {
                required: required,
                between: between(ACTIVATION_TIME_FROM, ACTIVATION_TIME_TO),
            },
            soundLevel: { required, between: between(MIN_SOUND_LEVEL, MAX_SOUND_LEVEL) },
            acousticGroup: ['activationTime', 'soundLevel'],
        };
    },

    watch: {
        shown() {
            this.initData();
        },

        soundLevel() {
            this.isChanged = true;
        },

        activationTime() {
            this.isChanged = true;
        },

        modelAcousticEnabled(val) {
            this.$nextTick(() => {
                this.isAcousticEnabled = val;
                this.isChanged = true;
            });
        },
    },

    created() {
        this.initData();
    },

    methods: {
        initData() {
            this.isAcousticEnabled = this.location.getAcousticStatus();
            let actTime = this.location.getActivationTime();
            this.activationTime = actTime >= ACTIVATION_TIME_FROM ? actTime : ACTIVATION_TIME_FROM;
            this.soundLevel = this.location.getSoundLevel();
            this.isChanged = false;
        },

        close() {
            this.isChanged = false;
            this.$emit('close');
        },

        ok() {
            if (!this.isOkDisabled) {
                this.$emit('ok', {
                    isAcousticEnabled: this.isAcousticEnabled,
                    activationTime: this.activationTime,
                    soundLevel: this.soundLevel,
                });
                this.close();
            }
        },

        toggleAcoustic() {
            this.$nextTick(() => {
                this.isAcousticEnabled = !this.isAcousticEnabled;
                this.isChanged = true;
            });
        },
    },
};
</script>
